import React, { useState, useEffect } from 'react';
import moment from 'moment';
import formatNumber from '../../../helpers/NumberFormat';
import ModalV3 from '../../shared/Modal/ModalV3';
import { FaEdit, FaRegFilePdf } from "react-icons/fa";
import { AiOutlineSchedule } from "react-icons/ai";
import { MdOutlineDeleteOutline } from "react-icons/md";
import { toast } from "react-toastify";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Logo from '../../../assets/images/logo.png';
import "./ScheduledPayments.scss";

const LOCAL_STORAGE_KEY = "scheduledPayments";

export default function ScheduledPaymentsComponent({ isMobile, mainProps, showSidebar }) {
    const { main } = mainProps;
    const [openModal, setOpenModal] = useState(false);
    const [editItemIndex, setEditItemIndex] = useState(null);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [scheduledPayments, setScheduledPayments] = useState([]);
    const [form, setForm] = useState({
        title: '',
        invoice: '',
        amount: '',
        expectedPaymentDate: '',
        paidDate: '',
        paidAmount: ''
    });

    useEffect(() => {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
            setScheduledPayments(JSON.parse(stored));
        }
    }, []);

    const saveToLocalStorage = (data) => {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
    };

    const resetForm = () => {
        setForm({ title: '', invoice: '', amount: '', expectedPaymentDate: '', paidDate: '', paidAmount: '' });
        setEditItemIndex(null);
    };

    const handleSubmit = () => {
        if (!form.title || !form.amount || !form.expectedPaymentDate) {
            toast.warn('Please ensure that the Title, Amount, and Expected Payment Date are filled in to schedule a payment.')
            return;
        }
        const updatedList = [...scheduledPayments];
        if (editItemIndex !== null) {
            updatedList[editItemIndex] = form;
        } else {
            updatedList.push(form);
        }
        setScheduledPayments(updatedList);
        saveToLocalStorage(updatedList);
        resetForm();
        toast.success('The scheduled payment has been successfully saved to this device.');
        setOpenModal(false);
    };

    const handleDelete = () => {
        const updatedList = scheduledPayments.filter((_, idx) => idx !== deleteIndex);
        setScheduledPayments(updatedList);
        saveToLocalStorage(updatedList);
        toast.success('The scheduled payment has been successfully deleted from this device.');
        setDeleteIndex(null);
    };

    const exportPDF = () => {
        const doc = new jsPDF();

        const img = new Image();
        img.src = Logo;
        img.onload = () => {
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 10;
            const logoWidth = 50;
            const logoHeight = 15;

            doc.setFontSize(16);
            doc.text(`Schedules Extract ${moment().format("DD/MM/YYYY")}`, margin, 20);

            const logoX = pageWidth - logoWidth - margin;
            const logoY = 10;
            doc.addImage(img, 'PNG', logoX, logoY, logoWidth, logoHeight);

            autoTable(doc, {
                startY: 30,
                head: [['Title', 'Invoice', 'Amount', 'Expected Date', 'Paid Date', 'Paid Amount']],
                body: scheduledPayments.map(tx => [
                    tx.title,
                    tx.invoice ?? '',
                    formatNumber(tx.amount) + ' EUR',
                    tx.expectedPaymentDate,
                    tx.paidDate ?? '',
                    formatNumber(tx.paidAmount) + ' EUR' ?? '',
                ]),
            });

            doc.save('My-schedules.pdf');
        }
    }

    return (
        <>
            <div className="container-fluid bg-col transition-width" style={!isMobile ? {
                width: showSidebar ? 'calc(100% - 332px)' : '100%',
                marginLeft: 0,
                marginRight: 'auto',
                transition: showSidebar ? 'width 0.3s ease-in-out' : 'width 0.16s ease-in-out'
            } : {}}>
                <div className="row p-3 reports-panel">
                    <h5 className="mb-5 mt-2" style={{ fontWeight: "500" }}>SCHEDULED PAYMENTS</h5>
                    <p className='text-muted'>This section you will have the opportunity to keep track of your scheduled payments.</p>

                    <div className="bg-custom-dark px-3 py-4 mb-3 rounded shadow-sm">
                        <h5 className="text-muted mb-4">Report Selection</h5>
                        <div className="row g-5">
                            <div className="col-md-6">
                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex justify-content-between">
                                        <span>Registered To:</span>
                                        <strong>{main?.user?.first_name} {main?.user?.last_name}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Account Number:</span>
                                        <strong>{main?.user?.detail?.account_number?.replace(/^.*?-/, '')}</strong>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex justify-content-between">
                                        <span>Available balance:</span>
                                        <strong>{formatNumber(main?.user?.balance)} €</strong>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Account balance:</span>
                                        <strong>{formatNumber(main?.user?.balance)} €</strong>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Credit limit:</span>
                                        <strong>{formatNumber(0)} €</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={`mt-5 ${isMobile ? "text-center" : "text-end"}`}>
                        <button className={`btn btn-primary ${isMobile ? "w-90" : "w-20"}`} onClick={() => {
                            resetForm();
                            setOpenModal(true);
                        }}>
                            <AiOutlineSchedule size={30} /> Add New Schedule
                        </button>
                    </div>

                    <div className="card p-3 my-5">

                        <div className="table-responsive mt-2">
                            <h5 className='mb-4'>SCHEDULED PAYMENTS LIST - MANUAL</h5>
                            <table className="table table-striped table-hover">
                                <thead className='table-head'>
                                    <tr>
                                        <th className='text-center'>#</th>
                                        <th>Payment Title</th>
                                        <th>#Invoice</th>
                                        <th className='text-end'>Amount</th>
                                        <th className='text-center'>Expected Date</th>
                                        <th className='text-center'>Paid Date</th>
                                        <th className='text-center'>Paid Amount</th>
                                        <th className='text-center'>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scheduledPayments.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="text-center text-muted py-4">
                                                You do not have any scheduled payments.
                                            </td>
                                        </tr>
                                    ) : (
                                        scheduledPayments.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className='text-center'>{idx + 1}</td>
                                                <td>{item.title}</td>
                                                <td>{item.invoice}</td>
                                                <td className='text-end'>{item.amount && formatNumber(item.amount)}{item.amount && '€'}</td>
                                                <td className='text-center'>{item.expectedPaymentDate && moment(item.expectedPaymentDate).format("DD/MM/YYYY")}</td>
                                                <td className='text-center'>{item.paidDate && moment(item.paidDate).format("DD/MM/YYYY")}</td>
                                                <td className='text-center'>{item.paidAmount && formatNumber(item.paidAmount)}{item.paidAmount && '€'}</td>
                                                <td className='text-center'>
                                                    <FaEdit
                                                        className="me-2 cursor-pointer"
                                                        onClick={() => {
                                                            setForm(item);
                                                            setEditItemIndex(idx);
                                                            setOpenModal(true);
                                                        }}
                                                        size={20}
                                                        title='Edit Payment'
                                                    />
                                                    <MdOutlineDeleteOutline
                                                        className="text-danger cursor-pointer"
                                                        onClick={() => setDeleteIndex(idx)}
                                                        size={25}
                                                        title='Delete Payment'
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {scheduledPayments.length > 0 && (
                            <div className="d-flex justify-content-end my-4 gap-5 mx-2">
                                <div className='download-links' onClick={exportPDF}><FaRegFilePdf style={{ fontSize: '30px' }} /> Download PDF</div>
                            </div>
                        )}
                    </div>

                    {scheduledPayments.length > 0 && (
                        <p className="text-muted small my-3">
                            <strong>Note:</strong> Your scheduled payments are stored securely on this device only. This means they won't be visible or accessible from other devices or browsers. If you clear your browser data or switch devices, this information will be lost.
                        </p>
                    )}

                    {openModal && (
                        <ModalV3 isOpen={openModal} onClose={() => setOpenModal(false)}>
                            <div className="p-3">
                                <div className='d-flex justify-content-between align-items-center mt-4'>
                                    <h5 className='mb-0'>{editItemIndex !== null ? 'Edit Schedule' : 'Add New Schedule'}</h5>
                                    <AiOutlineSchedule size={30} />
                                </div>
                                <div className="w97 m-auto my-3" style={{ borderBottom: "2px solid #EFEFEF" }}></div>
                                <form className="mt-4 d-flex flex-column gap-3">
                                    {["title", "invoice", "amount", "expectedPaymentDate", "paidDate", "paidAmount"].map((field) => {
                                        const isDateField = field.includes("Date");
                                        const isAmountField = field.toLowerCase().includes("amount");
                                        const label = field
                                            .replace(/([A-Z])/g, ' $1')
                                            .replace(/^./, str => str.toUpperCase());
                                        const today = moment().format("YYYY-MM-DD");

                                        return (
                                            <div key={field} className="mb-3">
                                                <label className="form-label">{label}</label>
                                                <input
                                                    className="form-control"
                                                    placeholder={label}
                                                    type={isDateField ? "date" : isAmountField ? "number" : "text"}
                                                    min={(isDateField && field === 'expectedPaymentDate') ? today : undefined}
                                                    // min={isDateField && undefined}
                                                    value={form[field]}
                                                    onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                                                />
                                            </div>
                                        );
                                    })}
                                    <div className="text-end mt-3">
                                        <button type="button" className="btn schedule-btn-primary me-2" onClick={() => setOpenModal(false)}>Cancel</button>
                                        <button type="button" className="btn schedule-btn-secondary" onClick={handleSubmit}>
                                            {editItemIndex !== null ? "Update" : "Add"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </ModalV3>
                    )}

                    {deleteIndex !== null && (
                        <ModalV3 isOpen={true} onClose={() => setDeleteIndex(null)}>
                            <div className="p-4 text-center">
                                <h5>Are you sure you want to delete this scheduled payment #{deleteIndex + 1} ?</h5>
                                <div className="w97 m-auto my-3" style={{ borderBottom: "2px solid #EFEFEF" }}></div>
                                <div className="mt-4 d-flex justify-content-center gap-3">
                                    <button className="btn schedule-btn-primary" onClick={() => setDeleteIndex(null)}>No</button>
                                    <button className="btn schedule-btn-secondary" onClick={handleDelete}>Yes, Delete</button>
                                </div>
                            </div>
                        </ModalV3>
                    )}
                </div>
            </div>
        </>
    );
}